import React, {useEffect, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay, Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import {Banner, getBannersBySection} from '../../api/commonApi';
import {isCoordinator} from '../../utils/authUtils';
import {useNavigate} from 'react-router-dom';

interface BannerSwiperProps {
    section: string; // 'MAIN' 또는 'SERVE'
    autoplayDelay?: number;
    loop?: boolean;
}

export default function BannerSwiper({
                                         section,
                                         autoplayDelay = 3000,
                                         loop = true
                                     }: BannerSwiperProps) {
    const navigate = useNavigate();
    const [banners, setBanners] = useState<Banner[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const response = await getBannersBySection(section);
                if (response.data.code === '00') {
                    // 노출 설정이 되어있고, 노출 기간 내에 있는 배너만 필터링
                    const now = new Date();
                    const activeBanners = response.data.data.filter(banner => {
                        if (!banner.displayYn) return false;

                        // 노출 기간 체크
                        if (banner.startAt && banner.endAt) {
                            const startDate = new Date(banner.startAt);
                            const endDate = new Date(banner.endAt);
                            return now >= startDate && now <= endDate;
                        }

                        // 상시 노출
                        return true;
                    });

                    // sortOrder로 정렬
                    activeBanners.sort((a, b) => a.sortOrder - b.sortOrder);
                    setBanners(activeBanners);
                }
            } catch (error) {
                console.error('Failed to fetch banners:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBanners();
    }, [section]);

    if (loading) {
        return (
            <section className="main-banner">
                <div className="swiper banner-slider">
                    <div style={{textAlign: 'center', padding: '50px 0'}}>
                        Loading...
                    </div>
                </div>
            </section>
        );
    }

    if (banners.length === 0) {
        return null; // 배너가 없으면 섹션 자체를 표시하지 않음
    }

    const handleBannerClick = (banner: Banner) => {
        if (!banner.linkUrl) return;

        // 외부 링크
        if (banner.linkUrl.startsWith('http://') || banner.linkUrl.startsWith('https://')) {
            window.open(banner.linkUrl, '_blank');
            return;
        }

        // 서비스 신청 관련 배너: 코디 회원은 일감찾기 화면으로 분기
        const isServiceApplyUrl = banner.linkUrl.includes('/service-apply') || banner.linkUrl.includes('/service/apply');
        if (isServiceApplyUrl && isCoordinator()) {
            navigate('/task/findTask');
            return;
        }

        navigate(banner.linkUrl);
    };

    return (
        <section className="main-banner">
            <div className="swiper banner-slider">
                <Swiper
                    modules={[Pagination, Autoplay]}
                    pagination={{clickable: true}}
                    slidesPerView={1}
                    spaceBetween={0}
                    autoplay={{
                        delay: autoplayDelay,
                        disableOnInteraction: false,
                    }}
                    speed={600}
                    loop={loop && banners.length > 1}
                >
                    {banners.map((banner) => (
                        <SwiperSlide key={banner.seq}>
                            <a
                                onClick={() => handleBannerClick(banner)}
                                style={{cursor: banner.linkUrl ? 'pointer' : 'default'}}
                            >
                                <img
                                    src={banner.bannerImageUrl}
                                    alt={banner.bannerName}
                                    onError={(e) => {
                                        // 이미지 로드 실패 시 기본 이미지 처리
                                        e.currentTarget.src = '/assets/images/default-banner.jpg';
                                    }}
                                />
                            </a>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>
    );
}